import {
  Box,
  Stack,
  Text,
  useCheckbox,
  useCheckboxGroup,
  UseCheckboxProps,
  VStack,
  Input,
  InputGroup,
  InputLeftElement,
  Flex,
  IconButton,
} from "@chakra-ui/react";
import { useMemo, useState, useCallback } from "react";
import { CheckboxCheckedIcon } from "../icons/CheckboxCheckedIcon";
import { CheckboxUncheckedIcon } from "../icons/CheckboxUncheckedIcon";
import { SearchIcon } from "../icons/SearchIcon";
import { CloseIcon } from "@chakra-ui/icons";
import { List } from "../../api/lists";
import { ENTITY_TYPES } from "../../screens/root/constants";

export type ListOption = {
  value: string;
  label: string;
  id: number;
};

interface SearchableMultiSelectProps {
  lists: List[];
  selectedListIds: number[];
  onSelectionChange: (listIds: number[]) => void;
  onRemoveFromList: (listId: number) => void;
  listType: "comics" | "releases";
  entityId: number;
}

export function SearchableMultiSelect({
  lists,
  selectedListIds,
  onSelectionChange,
  onRemoveFromList,
  listType,
  entityId,
}: SearchableMultiSelectProps) {
  const [searchTerm, setSearchTerm] = useState("");

  // Check if current entity is already in a list
  const isEntityInList = useCallback(
    (list: List): boolean => {
      if (!list.list_items || !Array.isArray(list.list_items)) {
        return false;
      }

      // Entity type IDs: 3 = Comic, 4 = Release (hardcoded based on backend)
      const expectedEntityTypeId =
        listType === "comics" ? ENTITY_TYPES.COMICS : ENTITY_TYPES.RELEASES;

      return list.list_items.some((item: any) => {
        return (
          item.context_entity_id === entityId &&
          item.context_entity_type_id === expectedEntityTypeId
        );
      });
    },
    [entityId, listType],
  );

  // Separate lists into those already containing the item vs those available to add to
  const { listsAlreadyIn, listsAvailableToAdd } = useMemo(() => {
    if (!lists || !Array.isArray(lists)) {
      return { listsAlreadyIn: [], listsAvailableToAdd: [] };
    }

    const alreadyIn: typeof lists = [];
    const availableToAdd: typeof lists = [];

    lists.forEach((list) => {
      if (isEntityInList(list)) {
        alreadyIn.push(list);
      } else {
        availableToAdd.push(list);
      }
    });

    return { listsAlreadyIn: alreadyIn, listsAvailableToAdd: availableToAdd };
  }, [lists, entityId, isEntityInList]);

  // Filter available lists by search term
  const filteredAvailableLists = useMemo(() => {
    if (!searchTerm) return listsAvailableToAdd;

    return listsAvailableToAdd.filter((list) =>
      list.display_name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [listsAvailableToAdd, searchTerm]);

  // Filter already-in lists by search term (for display)
  const filteredAlreadyInLists = useMemo(() => {
    if (!searchTerm) return listsAlreadyIn;

    return listsAlreadyIn.filter((list) =>
      list.display_name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [listsAlreadyIn, searchTerm]);

  // Convert available lists to options format (only these are selectable)
  const options: ListOption[] = useMemo(() => {
    return filteredAvailableLists.map((list) => ({
      value: list.id.toString(),
      label: list.display_name,
      id: list.id,
    }));
  }, [filteredAvailableLists]);

  const { getCheckboxProps } = useCheckboxGroup({
    value: selectedListIds.map((id) => id.toString()),
    onChange: (values: string[]) => {
      onSelectionChange(values.map((id) => parseInt(id, 10)));
    },
  });

  return (
    <VStack align="stretch" overflowY="hidden">
      <InputGroup>
        <InputLeftElement pointerEvents="none">
          <SearchIcon color="dune.100" w={4} h={4} />
        </InputLeftElement>
        <Input
          placeholder="Search lists..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          bg="transparent.white.10"
          border="1px solid"
          borderColor="transparent.white.20"
          color="neutral.200"
          _placeholder={{ color: "neutral.500" }}
          _focus={{
            borderColor: "blaze.300",
            bg: "transparent.white.20",
            outline: "none",
            boxShadow: "none",
          }}
          size="sm"
        />
      </InputGroup>

      <Stack
        spacing={0}
        flex="1"
        minHeight="0"
        maxHeight="200px"
        overflowY="auto"
      >
        {filteredAlreadyInLists.length === 0 && options.length === 0 ? (
          <Text color="neutral.500" fontSize="sm" textAlign="center" py={4}>
            {searchTerm ? "No lists found" : "No lists created yet"}
          </Text>
        ) : (
          <>
            {/* Show lists that already contain the item with remove buttons */}
            {filteredAlreadyInLists.map((list, i) => (
              <Box key={`already-${list.id}`}>
                <Box
                  px={2}
                  py={3}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  bg="transparent.white.5"
                  borderRadius="md"
                  _hover={{ bg: "transparent.white.10" }}
                >
                  <Text fontSize="sm" color="neutral.400">
                    {list.display_name}
                  </Text>
                  <Flex alignItems="center" gap={2}>
                    <Text fontSize="xs" color="blaze.300" fontWeight="500">
                      ✓ Added
                    </Text>
                    <IconButton
                      aria-label={`Remove from ${list.display_name}`}
                      icon={<CloseIcon />}
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => onRemoveFromList(list.id)}
                      _hover={{ bg: "red.500", color: "white" }}
                    />
                  </Flex>
                </Box>
                {(i < filteredAlreadyInLists.length - 1 ||
                  options.length > 0) && (
                  <Box w="full" h="1px" bg="transparent.white.10" />
                )}
              </Box>
            ))}

            {/* Show lists available to add to (selectable) */}
            {options.map((option, i) => (
              <Box key={option.value}>
                <CustomCheckbox {...getCheckboxProps({ value: option.value })}>
                  <Text
                    fontSize="sm"
                    color={
                      selectedListIds.includes(option.id)
                        ? "dune.100"
                        : "dune.700"
                    }
                    _groupHover={{ color: "dune.100" }}
                    flex="1"
                  >
                    {option.label}
                  </Text>
                </CustomCheckbox>
                {i < options.length - 1 && (
                  <Box w="full" h="1px" bg="transparent.white.10" />
                )}
              </Box>
            ))}
          </>
        )}
      </Stack>
    </VStack>
  );
}

interface CustomCheckboxProps extends UseCheckboxProps {
  children?: React.ReactNode;
}

function CustomCheckbox(props: CustomCheckboxProps) {
  const { state, getInputProps } = useCheckbox(props);
  const input = getInputProps();

  return (
    <Box
      layerStyle="filterList"
      as="label"
      cursor="pointer"
      role="group"
      px={2}
      display="flex"
      alignItems="center"
      gap={2}
    >
      <input {...input} hidden />
      {props.children}
      {state.isChecked ? (
        <CheckboxCheckedIcon color="blaze.300" w={5} h={5} flexShrink={0} />
      ) : (
        <CheckboxUncheckedIcon
          _groupHover={{ color: "transparent.white.80" }}
          color="transparent.white.60"
          w={5}
          h={5}
          flexShrink={0}
        />
      )}
    </Box>
  );
}
